import React, { ReactNode } from "react";
import { StyleSheet, View, Text } from "react-native";
import { colors, semanticColors, spacing, radii, typographyScale } from "../../../../styles/design-system";
import CustomText from "../../../common/general/CustomText/CustomText";
import { Menu } from "lucide-react";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import useResponsiveScreen from "../../../../hooks/useResponsiveScreen";
import { useWorkspaceThreads } from "../../../../contexts/CompanionThreadsContext";

interface WorkspaceHeaderProps {
  title?: string;
  badge?: string;
  badgeColor?: string;
  middleAction?: ReactNode;
  actions?: ReactNode[];
}

export const WorkspaceHeader = ({
  title,
  actions,
  badge,
  badgeColor = colors.grey[100],
  middleAction,
}: WorkspaceHeaderProps) => {
  const { isLarge } = useResponsiveScreen();
  const { isSidebarCollapsed, setIsSidebarCollapsed } = useWorkspaceThreads();

  return (
    <View style={[styles.workspaceHeader, !isLarge && styles.workspaceHeaderSmall]}>
      <View style={[styles.titleWrapper, !isLarge && styles.titleWrapperSmall]}>
        {!isLarge && (
          <CustomButton
            styleType="secondaryLightened"
            size="s"
            style={styles.sidebarIcon}
            leftIcon={<Menu size={20} color={colors.grey[600]} />}
            onPress={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            accessibilityLabel="Toggle sidebar"
            accessibilityHint="Toggle the sidebar visibility"
          />
        )}
        {isLarge && (
          <CustomText text={title} style={styles.titleText} useTranslationText={false} aria-level="1" role="heading" />
        )}
        {badge != null && (
          <View style={[styles.badge, { backgroundColor: badgeColor }]}>
            <Text style={styles.badgeContent}>{badge}</Text>
          </View>
        )}
      </View>
      {middleAction != null && middleAction}
      <View style={styles.actionRow}>{actions != null && actions.map((action) => action)}</View>
    </View>
  );
};

const styles = StyleSheet.create({
  workspaceHeader: {
    position: "relative",
    width: "100%",
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: spacing.lg,
    height: 57,
    backgroundColor: semanticColors.surface.white,
  },
  workspaceHeaderSmall: {
    paddingLeft: spacing.xxs,
    paddingRight: 12,
  },
  titleWrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  titleWrapperSmall: {
    gap: spacing.xs,
  },
  titleText: {
    fontWeight: 600,
    color: colors.grey[900],
    fontSize: typographyScale.textLg.fontSize,
    lineHeight: 30,
  },
  badge: {
    borderRadius: radii.full,
    paddingHorizontal: 10,
    paddingVertical: 2,
    backgroundColor: colors.grey[100],
    lineHeight: typographyScale.textSm.lineHeight,
  },
  badgeContent: {
    fontWeight: 600,
    color: colors.grey[900],
    fontSize: typographyScale.textXs.fontSize,
  },
  actionRow: {
    flexShrink: 0,
    display: "flex",
    flexDirection: "row",
    gap: 12,
    alignContent: "center",
  },
  sidebarIcon: {
    borderRadius: "50%",
  },
});
